/**
 * Painel oculto para controlar o auto-shutdown do back via Lambda.
 *
 * Endpoint coberto: `/api/scheduler` (Vercel Serverless Function), que assina
 * as credenciais IAM server-side e invoca a Lambda `optimus-scheduler`.
 *
 * Acesso: rota não-linkada `/_/scheduler` — security through obscurity.
 * Não há gate de senha, mas o "poder" do painel é apenas start/stop/status
 * dos recursos AWS (EC2 + RDS) — sem acesso a dados. Em caso de URL vazada,
 * basta rotacionar as credenciais IAM da env var da Vercel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageMeta from '../../components/common/PageMeta'
import {
  callScheduler,
  type SchedulerAction,
  type SchedulerResponse,
} from '../../services/awsSchedulerService'

interface LogEntry {
  timestamp: string
  action: SchedulerAction
  payload: SchedulerResponse | { error: string }
  ok: boolean
}

const STATUS_REFRESH_MS = 30_000

function formatState(state: string | undefined): {
  label: string
  className: string
} {
  switch (state) {
    case 'running':
    case 'available':
      return {
        label: state,
        className: 'text-emerald-600 dark:text-emerald-400',
      }
    case 'pending':
    case 'starting':
      return { label: state, className: 'text-amber-600 dark:text-amber-400' }
    case 'stopping':
      return { label: state, className: 'text-orange-600 dark:text-orange-400' }
    case 'stopped':
      return { label: state, className: 'text-gray-500 dark:text-gray-400' }
    default:
      return { label: state ?? '—', className: 'text-gray-500' }
  }
}

export default function HiddenSchedulerPanel() {
  const [status, setStatus] = useState<SchedulerResponse | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [busy, setBusy] = useState<SchedulerAction | null>(null)
  const [log, setLog] = useState<LogEntry[]>([])
  const intervalRef = useRef<number | null>(null)

  const ec2 = useMemo(() => formatState(status?.ec2.state), [status])
  const rds = useMemo(() => formatState(status?.rds.state), [status])

  const refreshStatus = useCallback(async () => {
    try {
      const data = await callScheduler('status')
      setStatus(data)
      setStatusError(null)
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'erro desconhecido')
    }
  }, [])

  const runAction = useCallback(
    async (action: SchedulerAction) => {
      if (busy) return
      setBusy(action)
      const timestamp = new Date().toISOString()
      try {
        const payload = await callScheduler(action)
        setLog(prev =>
          [{ timestamp, action, payload, ok: true }, ...prev].slice(0, 10)
        )
        if (action !== 'status') {
          // Atualiza a barra de status logo após start/stop
          await refreshStatus()
        } else {
          setStatus(payload)
        }
      } catch (err) {
        setLog(prev =>
          [
            {
              timestamp,
              action,
              payload: {
                error: err instanceof Error ? err.message : 'erro desconhecido',
              },
              ok: false,
            },
            ...prev,
          ].slice(0, 10)
        )
      } finally {
        setBusy(null)
      }
    },
    [busy, refreshStatus]
  )

  // Status inicial + polling automático a cada 30s.
  useEffect(() => {
    void refreshStatus()
    intervalRef.current = window.setInterval(refreshStatus, STATUS_REFRESH_MS)
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [refreshStatus])

  return (
    <>
      <PageMeta
        title="Scheduler Control"
        description="Painel oculto de controle do Optimus"
      />
      <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-950">
        <div className="mx-auto max-w-2xl space-y-6">
          <header>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Scheduler Control
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Acesso direto à Lambda <code>optimus-scheduler</code>. Status
              atualiza a cada {STATUS_REFRESH_MS / 1000}s.
            </p>
          </header>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              Estado atual
            </h2>
            {statusError ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                Falha ao consultar: {statusError}
              </p>
            ) : status === null ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Consultando...
              </p>
            ) : (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-gray-600 dark:text-gray-400">EC2</dt>
                  <dd className={`font-mono ${ec2.className}`}>{ec2.label}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-gray-600 dark:text-gray-400">
                    IP público
                  </dt>
                  <dd className="font-mono text-gray-700 dark:text-gray-300">
                    {status.ec2.ip ?? '—'}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-gray-600 dark:text-gray-400">RDS</dt>
                  <dd className={`font-mono ${rds.className}`}>{rds.label}</dd>
                </div>
              </dl>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              Ações
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void runAction('start')}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy === 'start' ? 'Iniciando...' : 'Iniciar'}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  if (
                    window.confirm(
                      'Confirmar shutdown da EC2 + RDS? Usuários ativos serão desconectados.'
                    )
                  ) {
                    void runAction('stop')
                  }
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy === 'stop' ? 'Desligando...' : 'Desligar'}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void runAction('status')}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {busy === 'status' ? 'Consultando...' : 'Atualizar status'}
              </button>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              Histórico recente
            </h2>
            {log.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhuma ação executada nesta sessão.
              </p>
            ) : (
              <ol className="space-y-3 text-sm">
                {log.map(entry => (
                  <li
                    key={entry.timestamp}
                    className={`rounded-md border-l-2 px-3 py-2 ${
                      entry.ok
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'border-red-500 bg-red-50 dark:bg-red-950/30'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium uppercase">
                        {entry.action}
                      </span>
                      <time>{new Date(entry.timestamp).toLocaleString()}</time>
                    </div>
                    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-gray-700 dark:text-gray-300">
                      {JSON.stringify(entry.payload, null, 2)}
                    </pre>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </div>
    </>
  )
}
